import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useEffect } from 'react';

import { useEmitirInformeFinal } from '@/features/informes/hooks/useInformes';
import { useNotify } from '@/hooks/useNotify';
import { isApiError } from '@/lib/apiError';

interface CerrarProtocoloDialogProps {
  open: boolean;
  onClose: () => void;
  uidProtocolo: string;
  numeroProtocolo: number;
}

export function CerrarProtocoloDialog({
  open,
  onClose,
  uidProtocolo,
  numeroProtocolo,
}: CerrarProtocoloDialogProps): JSX.Element {
  const notify = useNotify();
  const emitirFinal = useEmitirInformeFinal();

  useEffect(() => {
    if (!open) emitirFinal.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  async function handleConfirmar(): Promise<void> {
    try {
      await emitirFinal.mutateAsync(uidProtocolo);
      notify.success('Protocolo cerrado. Informe final generado.');
      onClose();
    } catch (err) {
      if (isApiError(err) && err.errorCode === 'informe_final_ya_emitido') {
        notify.error('Este protocolo ya tiene informe final emitido.');
      } else if (isApiError(err) && err.errorCode === 'protocolo_sin_analisis') {
        notify.error('No se puede cerrar un protocolo sin análisis cargados.');
      } else {
        notify.error('Error al cerrar el protocolo. Intentá nuevamente.');
      }
    }
  }

  return (
    <Dialog
      open={open}
      onClose={emitirFinal.isPending ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Cerrar protocolo</DialogTitle>

      <DialogContent>
        <Typography variant="body2" gutterBottom>
          Estás por cerrar el <strong>Protocolo N° {numeroProtocolo}</strong>.
        </Typography>
        <Typography variant="body2" gutterBottom>
          Se va a generar el informe final. Después del cierre, el protocolo queda bloqueado
          para edición y eliminación.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          El informe no se envía en esta acción. Podés enviarlo por email o WhatsApp después.
        </Typography>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={emitirFinal.isPending}>
          Cancelar
        </Button>
        <Button
          onClick={() => void handleConfirmar()}
          variant="contained"
          color="primary"
          disabled={emitirFinal.isPending}
          startIcon={
            emitirFinal.isPending ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <LockOutlinedIcon />
            )
          }
        >
          Cerrar protocolo
        </Button>
      </DialogActions>
    </Dialog>
  );
}
